/**
 * Where a phone menu or popover opens (spec 14.2). Below its button when it
 * fits, above when it does not, and in any case wholly on screen and inside
 * the safe areas. Right edge on the button's right edge where there is room.
 * Pure, for unit tests.
 */
export interface Box { top: number; bottom: number; left: number; right: number }
export interface Edges { top: number; right: number; bottom: number; left: number }

export function placeMenu(
  anchor: Box,
  size: { w: number; h: number },
  view: { w: number; h: number },
  insets: Edges,
  gap = 4,
  margin = 8,
): { top: number; left: number; up: boolean } {
  const minX = insets.left + margin;
  const maxX = view.w - insets.right - margin - size.w;
  const left = Math.max(minX, Math.min(anchor.right - size.w, maxX));
  const minY = insets.top + margin;
  const maxY = view.h - insets.bottom - margin - size.h;
  const below = anchor.bottom + gap;
  if (below <= maxY) return { top: below, left, up: false };
  const above = anchor.top - gap - size.h;
  if (above >= minY) return { top: above, left, up: true };
  // Taller than either side: as much of it on screen as there is room for.
  return { top: Math.max(minY, Math.min(below, maxY)), left, up: false };
}

/** The visible area and a ref callback that places the element on mount, before paint. */
export function placeOnMount(anchor: Box | null, insets: Edges): (el: HTMLElement | null) => void {
  return (el) => {
    if (!el || !anchor || typeof window === 'undefined') return;
    const r = el.getBoundingClientRect();
    const vv = window.visualViewport;
    const view = { w: vv ? vv.width : window.innerWidth, h: vv ? vv.height : window.innerHeight };
    const p = placeMenu(anchor, { w: r.width, h: r.height }, view, insets);
    el.style.top = `${p.top}px`;
    el.style.left = `${p.left}px`;
    el.style.right = 'auto';
    el.style.bottom = 'auto';
  };
}
